import { Text, useToastContext } from "@ttc3k/trekker";
import {
  EnumUserEmailPreferencesFrequency,
  EnumUserEmailPreferencesTemplateName,
  useUserUpdateMutation,
} from "gql/generated";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Flex } from "styled-system/jsx";
import { SwitchCard } from "shared/components/molecules/SwitchCard";
import { useAppContext } from "useAppContext";

type NotificationPreferencesProps = {
  focusOnMount?: boolean;
};

export const NotificationPreferences = ({
  focusOnMount = false,
}: NotificationPreferencesProps) => {
  const { t } = useTranslation("people", {
    keyPrefix: "PERSON_CONTACT_INFO_MODAL.NOTIFICATIONS",
  });
  const { t: tCore } = useTranslation("core");
  const { toastFactory } = useToastContext();
  const { dbUser, refetchDbUser } = useAppContext();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [updateUser] = useUserUpdateMutation();
  const [saving, setSaving] = useState(false);

  const savedEnabled =
    dbUser?.emailPreferences?.find(
      preference =>
        !!preference &&
        preference.templateName ===
          EnumUserEmailPreferencesTemplateName.Broadcast &&
        !preference.entity?._entityId,
    )?.frequency !== EnumUserEmailPreferencesFrequency.Never;

  const [enabled, setEnabled] = useState(savedEnabled);

  useEffect(() => {
    setEnabled(savedEnabled);
  }, [savedEnabled]);

  useEffect(() => {
    if (!focusOnMount) return;
    sectionRef.current?.scrollIntoView({ block: "start" });
  }, [focusOnMount]);

  if (!dbUser?._id) return null;

  const handleChange = async (checked: boolean) => {
    const previous = enabled;
    setEnabled(checked);
    setSaving(true);
    const nextFrequency = checked
      ? EnumUserEmailPreferencesFrequency.Always
      : EnumUserEmailPreferencesFrequency.Never;
    const current = (dbUser.emailPreferences ?? []).flatMap(preference => {
      if (!preference?.templateName || !preference.frequency) return [];
      return [
        {
          templateName: preference.templateName,
          frequency: preference.frequency,
          ...(preference.entity?._entityId && preference.entity.type
            ? {
                entity: {
                  type: preference.entity.type,
                  _entityId: preference.entity._entityId,
                },
              }
            : {}),
        },
      ];
    });
    const hasBroadcastPreference = current.some(
      preference =>
        preference.templateName ===
          EnumUserEmailPreferencesTemplateName.Broadcast && !preference.entity,
    );
    const emailPreferences = hasBroadcastPreference
      ? current.map(preference =>
          preference.templateName ===
            EnumUserEmailPreferencesTemplateName.Broadcast && !preference.entity
            ? { ...preference, frequency: nextFrequency }
            : preference,
        )
      : [
          ...current,
          {
            templateName: EnumUserEmailPreferencesTemplateName.Broadcast,
            frequency: nextFrequency,
          },
        ];

    try {
      const result = await updateUser({
        variables: {
          id: dbUser._id,
          record: { emailPreferences },
        },
      });
      if (result.errors?.length) {
        throw new Error(result.errors[0]?.message);
      }
      void refetchDbUser();
    } catch {
      setEnabled(previous);
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description: t("ERROR"),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={sectionRef}>
      <Flex flexDir="column" gap="200">
        <Text visual="bodyMedium" color="text.dark">
          {t("TITLE")}
        </Text>
        <SwitchCard
          title={t("BROADCAST_LABEL")}
          subtitle={t("BROADCAST_HINT")}
          checked={enabled}
          disabled={saving}
          onCheckedChange={checked => {
            void handleChange(checked);
          }}
        />
      </Flex>
    </div>
  );
};
